/**
 * Roster sheet import / export for bulk team + assessment fill.
 * Capability / skills text is imported. Per-skill star ratings and progress
 * are never imported — those are set in the app after import.
 */
import * as XLSX from "xlsx";
import { SNS_CAMPUSES, getCampusByName } from "../constants/snsCampuses";

export const ROSTER_HEADERS = [
  "Team Name",
  "Problem Statement",
  "Name",
  "Roll Number",
  "Email",
  "Campus",
  "Department",
  "Year (college 1-4)",
  "Semester (college 1-8)",
  "School Class (1-12)",
  "Learning Pace (1-5)",
  "Capability",
  "Difficulties",
  "Interest",
  "Dislikes",
  "Domain",
  "Individual Goals",
  "Target Company",
  "Target Role",
  "Study Medium",
  "LeetCode Username",
];

const HEADER_ALIASES = {
  teamName: ["team name", "team", "group", "group name", "squad"],
  problemStatement: [
    "problem statement",
    "problem",
    "project problem",
    "ps",
    "problem text",
    "team problem",
  ],
  name: ["name", "member name", "student name", "full name"],
  rollNumber: ["roll number", "roll", "roll no", "rollno", "reg no", "register number"],
  email: ["email", "mail", "e-mail"],
  college: ["campus", "college", "institution", "school"],
  department: ["department", "dept", "branch"],
  collegeYear: [
    "year",
    "college year",
    "ug year",
    "year of study",
    "year (college 1-4)",
  ],
  semester: ["semester", "sem", "semester (college 1-8)"],
  schoolGrade: [
    "grade",
    "class",
    "school grade",
    "std",
    "school class",
    "school class (1-12)",
  ],
  skillLevel: [
    "skill level",
    "skill",
    "pace",
    "level",
    "learning pace",
    "learning pace (1-5)",
    "learner pace",
    "overall pace",
    "classroom pace",
  ],
  capability: ["capability", "capabilities", "strengths", "strength"],
  difficulties: ["difficulties", "difficulty", "weak areas", "weakness", "challenges"],
  interest: ["interest", "interests", "likes", "hobbies"],
  dislikes: ["dislikes", "dislike", "avoid"],
  domain: ["domain", "career path", "path", "focus area"],
  assessment: ["individual goals", "goals", "assessment", "personal goals"],
  targetCompany: ["target company", "company", "dream company"],
  targetRole: ["target role", "role", "job role"],
  studyMedium: [
    "study medium",
    "medium",
    "language",
    "preferred language",
    "medium language",
    "tamil medium",
  ],
  leetcodeUsername: [
    "leetcode username",
    "leetcode",
    "lc username",
    "leetcode handle",
    "leetcode id",
  ],
};

function normHeader(h) {
  return String(h || "")
    .trim()
    .toLowerCase()
    .replace(/[–—]/g, "-")
    .replace(/\s+/g, " ");
}

function mapHeaders(row0) {
  const map = {};
  (row0 || []).forEach((h, i) => {
    const n = normHeader(h);
    if (!n) return;
    for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
      if (map[field] != null) continue;
      if (aliases.some((a) => n === a || n.startsWith(`${a} (`))) {
        map[field] = i;
      }
    }
  });
  return map;
}

function cell(row, idx) {
  if (idx == null || idx < 0) return "";
  const v = row?.[idx];
  if (v == null) return "";
  return String(v).trim();
}

function blankMember() {
  const campus = SNS_CAMPUSES[0];
  return {
    name: "",
    rollNumber: "",
    email: "",
    targetRole: "",
    targetCompany: "",
    college: campus?.name || "",
    collegeYear: 2,
    semester: 3,
    schoolGrade: 10,
    department: campus?.departments?.[0] || "Other",
    skillLevel: 3,
    capability: "",
    difficulties: "",
    interest: "",
    dislikes: "",
    domain: "",
    assessment: "",
    studyMedium: "auto",
    leetcodeUsername: "",
    teamName: "",
    problemStatement: "",
    subjectPreferences: [],
    skillRatings: {},
    itemRatings: {
      capability: {},
      difficulties: {},
      interest: {},
      dislikes: {},
      domain: {},
    },
  };
}

function parseSkill(v) {
  const raw = String(v || "").trim().toLowerCase();
  if (!raw) return 3;
  if (/fast\+|very\s*fast|fast\s*plus/.test(raw)) return 5;
  if (/\bfast\b/.test(raw)) return 4;
  if (/\bslow\b/.test(raw)) return 1;
  if (/\bsteady\b/.test(raw)) return 2;
  if (/\baverage\b|\bstandard\b/.test(raw)) return 3;
  const n = Number(raw.replace(/[^\d.]/g, ""));
  if (!Number.isFinite(n)) return 3;
  return Math.min(5, Math.max(1, Math.round(n)));
}

function rowToMember(row, col) {
  const m = blankMember();
  m.name = cell(row, col.name);
  m.rollNumber = cell(row, col.rollNumber);
  m.email = cell(row, col.email);
  m.capability = cell(row, col.capability);
  m.skillRatings = {};
  m.itemRatings = {
    capability: {},
    difficulties: {},
    interest: {},
    dislikes: {},
    domain: {},
  };
  m.difficulties = cell(row, col.difficulties);
  m.interest = cell(row, col.interest);
  m.dislikes = cell(row, col.dislikes);
  m.domain = cell(row, col.domain);
  m.assessment = cell(row, col.assessment);
  m.targetCompany = cell(row, col.targetCompany);
  m.targetRole = cell(row, col.targetRole);
  m.skillLevel = parseSkill(cell(row, col.skillLevel));
  m.leetcodeUsername = cell(row, col.leetcodeUsername);
  m.teamName = cell(row, col.teamName);
  m.problemStatement = cell(row, col.problemStatement);
  const mediumRaw = cell(row, col.studyMedium).toLowerCase();
  if (mediumRaw) {
    if (/tamil|^ta\b/.test(mediumRaw)) m.studyMedium = "ta";
    else if (/hindi|^hi\b/.test(mediumRaw)) m.studyMedium = "hi";
    else if (/telugu|^te\b/.test(mediumRaw)) m.studyMedium = "te";
    else if (/kannada|^kn\b/.test(mediumRaw)) m.studyMedium = "kn";
    else if (/malayalam|^ml\b/.test(mediumRaw)) m.studyMedium = "ml";
    else if (/english|^en\b/.test(mediumRaw)) m.studyMedium = "en";
    else m.studyMedium = "auto";
  }

  const campusName = cell(row, col.college);
  if (campusName) {
    const campus = getCampusByName(campusName) || SNS_CAMPUSES.find(
      (c) => String(c.name).toLowerCase().includes(campusName.toLowerCase())
    );
    if (campus) {
      m.college = campus.name;
      const dept = cell(row, col.department);
      m.department =
        (campus.departments || []).find(
          (d) => String(d).toLowerCase() === dept.toLowerCase()
        ) ||
        dept ||
        campus.departments?.[0] ||
        m.department;
      if (campus.kind === "school") {
        const g = Number(cell(row, col.schoolGrade));
        m.schoolGrade = Number.isFinite(g) ? Math.min(12, Math.max(1, g)) : 10;
        m.collegeYear = null;
        m.semester = null;
      } else {
        const y = Number(cell(row, col.collegeYear));
        const s = Number(cell(row, col.semester));
        m.collegeYear = Number.isFinite(y) && y >= 1 && y <= 4 ? y : 2;
        m.semester = Number.isFinite(s) ? s : m.collegeYear * 2 - 1;
        m.schoolGrade = null;
      }
    } else {
      m.college = campusName;
      const dept = cell(row, col.department);
      if (dept) m.department = dept;
      const y = Number(cell(row, col.collegeYear));
      const s = Number(cell(row, col.semester));
      if (Number.isFinite(y)) m.collegeYear = y;
      if (Number.isFinite(s)) m.semester = s;
    }
  } else {
    const dept = cell(row, col.department);
    if (dept) m.department = dept;
    const y = Number(cell(row, col.collegeYear));
    const s = Number(cell(row, col.semester));
    if (Number.isFinite(y)) m.collegeYear = y;
    if (Number.isFinite(s)) m.semester = s;
  }

  return m;
}

/**
 * Parse an uploaded .xlsx / .xls / .csv File into roster data.
 */
export async function parseRosterSheet(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });

  let teamName = "";
  let sharedAssessment = "";
  let mode = "team"; // team | individuals

  const teamSheet =
    wb.Sheets.Team ||
    wb.Sheets.team ||
    wb.Sheets.TEAM ||
    wb.Sheets.Meta ||
    null;
  if (teamSheet) {
    const teamRows = XLSX.utils.sheet_to_json(teamSheet, { header: 1, defval: "" });
    for (const r of teamRows) {
      const k = normHeader(r[0]);
      const v = String(r[1] ?? "").trim();
      if (!k) continue;
      if (k.includes("team name") || k === "name" || k === "team") teamName = v || teamName;
      if (k.includes("shared") || k.includes("goal") || k.includes("assessment")) {
        sharedAssessment = v || sharedAssessment;
      }
      if (k === "mode" || k.includes("roster mode") || k.includes("generate mode")) {
        const mv = v.toLowerCase();
        if (mv.includes("individual") || mv.includes("solo") || mv.includes("batch")) {
          mode = "individuals";
        } else if (mv.includes("team")) {
          mode = "team";
        }
      }
    }
  }

  const membersSheetName =
    ["Members", "members", "Roster", "roster", "Students", "students"].find((n) => wb.Sheets[n]) ||
    wb.SheetNames[0];
  const sheet = wb.Sheets[membersSheetName];
  if (!sheet) throw new Error("No sheet found in the uploaded file");

  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
  if (!rows.length) throw new Error("Sheet is empty");

  const col = mapHeaders(rows[0]);
  if (col.name == null) {
    throw new Error('Missing "Name" column. Download the template and fill it.');
  }

  const members = [];
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row || !row.some((c) => String(c || "").trim())) continue;
    const m = rowToMember(row, col);
    if (!m.name) continue;
    members.push(m);
  }

  if (!members.length) throw new Error("No named members found in the sheet");

  // Fallback Team-sheet name onto rows that left Team Name blank
  if (teamName) {
    members.forEach((m) => {
      if (!String(m.teamName || "").trim()) m.teamName = teamName;
    });
  }

  const header = (rows[0] || []).map(normHeader);
  const sharedIdx = header.findIndex(
    (h) => h === "shared goals" || h === "team goals" || h === "shared assessment"
  );
  if (!sharedAssessment && sharedIdx >= 0) {
    for (let i = 1; i < rows.length; i++) {
      const v = String(rows[i][sharedIdx] || "").trim();
      if (v) {
        sharedAssessment = v;
        break;
      }
    }
  }

  const teams = groupRosterTeams(members);
  const namedTeams = teams.filter((t) => t.key !== "Unassigned" && t.members.length);
  if (namedTeams.length >= 2) {
    teamName = namedTeams[0].name;
    mode = "team";
  } else if (namedTeams.length === 1 && !teamName) {
    teamName = namedTeams[0].name;
  }

  return {
    members,
    teamName,
    sharedAssessment,
    sheetName: membersSheetName,
    mode,
    teams,
  };
}

/** Group imported members by Team Name. Each group keeps its own problem statement. */
export function groupRosterTeams(members = []) {
  const named = (members || []).filter((m) => String(m?.name || "").trim());
  const map = new Map();
  named.forEach((m) => {
    const raw = String(m.teamName || "").trim();
    const key = raw || "Unassigned";
    if (!map.has(key)) {
      map.set(key, {
        key,
        name: raw,
        problemStatement: "",
        members: [],
      });
    }
    const g = map.get(key);
    g.members.push(m);
    const ps = String(m.problemStatement || "").trim();
    if (ps && !g.problemStatement) g.problemStatement = ps;
  });
  return [...map.values()];
}

export function isMultiTeamRoster(members = []) {
  return groupRosterTeams(members).filter((t) => t.members.length).length >= 2;
}

export function buildRosterWorkbook(members = [], opts = {}) {
  const list = (members || []).filter((m) => m && (m.name || "").trim());
  const rows = [ROSTER_HEADERS];
  list.forEach((m) => {
    const campus = getCampusByName(m.college);
    const school = campus?.kind === "school";
    rows.push([
      m.teamName || opts.teamName || "",
      m.problemStatement || opts.problemStatement || "",
      m.name || "",
      m.rollNumber || "",
      m.email || "",
      m.college || "",
      m.department || "",
      school ? "" : (m.collegeYear ?? ""),
      school ? "" : (m.semester ?? ""),
      school ? (m.schoolGrade ?? "") : "",
      m.skillLevel ?? 3,
      m.capability || "",
      m.difficulties || "",
      m.interest || "",
      m.dislikes || "",
      m.domain || "",
      m.assessment || "",
      m.targetCompany || "",
      m.targetRole || "",
      m.studyMedium || "auto",
      m.leetcodeUsername || "",
    ]);
  });

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws["!cols"] = ROSTER_HEADERS.map((h) => ({
    wch: Math.max(14, Math.min(32, h.length + 3)),
  }));
  XLSX.utils.book_append_sheet(wb, ws, "Members");

  const guide = [
    ["Column", "Who fills it", "What it means", "Example"],
    [
      "Team Name",
      "Teams",
      "Same name = same team. Add as many distinct team names as you need — each can have its own problem statement. Leave blank for one team.",
      "Team Alpha",
    ],
    [
      "Problem Statement",
      "Teams",
      "The project problem for THAT team. Repeat the same text on every row of the team. Different teams must have different problems.",
      "Build a campus lost-and-found app…",
    ],
    ["Year (college 1-4)", "College only", "UG year 1–4. Leave blank for school students. Not school class.", "2"],
    ["Semester (college 1-8)", "College only", "Semester 1–8. Leave blank for school students.", "3"],
    [
      "School Class (1-12)",
      "School only",
      "Class / std 1–12. This is NOT exam marks or GPA. Leave blank for college students.",
      "10",
    ],
    [
      "Learning Pace (1-5)",
      "Everyone",
      "Overall classroom pace for the day plan — not marks, and not per-skill stars. 1=Slow (needs more time), 2=Steady, 3=Average, 4=Fast, 5=Fast+. Per-skill ratings are set in the app after import.",
      "3",
    ],
    ["Capability", "Everyone", "Skills / strengths as text, separated by semicolons. Stars are not in this sheet.", "Python; Communication"],
    ["Study Medium", "Optional", "auto / ta / en / hi — or Tamil / English / Hindi.", "auto"],
  ];
  const guideWs = XLSX.utils.aoa_to_sheet(guide);
  guideWs["!cols"] = [{ wch: 26 }, { wch: 16 }, { wch: 72 }, { wch: 28 }];
  XLSX.utils.book_append_sheet(wb, guideWs, "How to fill");

  const mode = opts.mode === "individuals" ? "Individuals" : "Team";
  const teamRows = [
    ["Field", "Value"],
    ["Mode", mode],
    ["Team Name", opts.mode === "individuals" ? "" : (opts.teamName || "")],
    ["Shared Goals", opts.mode === "individuals" ? "" : (opts.sharedAssessment || opts.assessment || "")],
    [
      "Notes",
      opts.mode === "individuals"
        ? "Each row = one student. Year+Semester = college. School Class = school std 1–12 (not marks). Learning Pace 1–5 = Slow→Fast classroom speed (not skill stars)."
        : "One team or many. Put Team Name + Problem Statement on each member row. Generate builds one plan per distinct team name.",
    ],
  ];
  const teamWs = XLSX.utils.aoa_to_sheet(teamRows);
  teamWs["!cols"] = [{ wch: 18 }, { wch: 56 }];
  XLSX.utils.book_append_sheet(wb, teamWs, "Team");

  return wb;
}

export function downloadRosterTemplate() {
  const example = blankMember();
  example.name = "Arun Kumar";
  example.rollNumber = "21CSE001";
  example.email = "arun@college.edu";
  example.department = "CSE";
  example.collegeYear = 2;
  example.semester = 3;
  example.schoolGrade = "";
  example.skillLevel = 3;
  example.capability = "Communication; Python";
  example.difficulties = "Public speaking anxiety";
  example.interest = "Cricket; Hackathons";
  example.dislikes = "Long lectures";
  example.domain = "EdTech";
  example.assessment = "Target 6–8 LPA";
  example.teamName = "Team Alpha";
  example.problemStatement = "Campus lost-and-found: students cannot recover misplaced IDs and devices quickly.";

  const example2 = {
    ...blankMember(),
    name: "Priya S",
    rollNumber: "21CSE014",
    department: "CSE",
    collegeYear: 2,
    semester: 3,
    schoolGrade: "",
    skillLevel: 3,
    capability: "Teamwork",
    interest: "Dance",
    domain: "FinTech",
    teamName: "Team Alpha",
    problemStatement: example.problemStatement,
  };

  const wb = buildRosterWorkbook([example, example2], {
    mode: "team",
    teamName: "Team Alpha",
    sharedAssessment: "Shared target 6–10 LPA · AWS CCP",
    problemStatement: example.problemStatement,
  });
  XLSX.writeFile(wb, "team_roster_template.xlsx");
}

/** Any number of teams — each member row carries Team Name + Problem Statement. */
export function downloadTeamsTemplate() {
  const alphaPs = "Campus lost-and-found: students cannot recover misplaced IDs and devices quickly.";
  const betaPs = "Canteen wait times: peak-hour queues waste 20+ minutes and food demand is unpredictable.";
  const gammaPs = "Library seating: students cannot find a free desk during peak hours.";
  const mk = (over) => ({ ...blankMember(), collegeYear: 2, semester: 3, schoolGrade: "", department: "CSE", ...over });
  const rows = [
    mk({
      name: "Arun Kumar",
      rollNumber: "21CSE001",
      email: "arun@college.edu",
      teamName: "Team Alpha",
      problemStatement: alphaPs,
      capability: "Python; Communication",
      interest: "Cricket",
      domain: "EdTech",
    }),
    mk({
      name: "Priya S",
      rollNumber: "21CSE014",
      teamName: "Team Alpha",
      problemStatement: alphaPs,
      capability: "Teamwork",
      interest: "Dance",
      domain: "EdTech",
    }),
    mk({
      name: "Vikram R",
      rollNumber: "21IT022",
      teamName: "Team Beta",
      problemStatement: betaPs,
      capability: "DSA basics",
      interest: "Hackathons",
      domain: "Campus ops",
    }),
    mk({
      name: "Meera N",
      rollNumber: "21IT031",
      teamName: "Team Beta",
      problemStatement: betaPs,
      capability: "UI design",
      interest: "Music",
      domain: "Campus ops",
    }),
    mk({
      name: "Karthik M",
      rollNumber: "21ECE008",
      teamName: "Team Gamma",
      problemStatement: gammaPs,
      capability: "IoT; Sensors",
      interest: "Robotics",
      domain: "Campus ops",
    }),
    mk({
      name: "Divya P",
      rollNumber: "21ECE019",
      teamName: "Team Gamma",
      problemStatement: gammaPs,
      capability: "Data viz",
      interest: "Design",
      domain: "Campus ops",
    }),
  ];
  const wb = buildRosterWorkbook(rows, {
    mode: "team",
    teamName: "Team Alpha",
    sharedAssessment: "Each team solves its own mapped problem statement. Add or remove team names as needed.",
  });
  XLSX.writeFile(wb, "teams_roster_template.xlsx");
}

/** Batch solo: many students, each gets their own schedule on Generate */
export function downloadIndividualsTemplate() {
  const rows = [];
  for (let i = 1; i <= 3; i++) {
    const m = blankMember();
    m.name = i === 1 ? "Arun Kumar" : i === 2 ? "Priya S" : "Vikram R";
    m.rollNumber = `21CSE00${i}`;
    m.department = "CSE";
    m.collegeYear = 3;
    m.semester = 5;
    m.schoolGrade = "";
    m.skillLevel = i === 1 ? 3 : i === 2 ? 4 : 2;
    m.capability = i === 1 ? "Python; Communication" : i === 2 ? "UI design; Teamwork" : "DSA basics";
    m.difficulties = i === 1 ? "Public speaking anxiety" : "Gets stuck alone";
    m.interest = i === 1 ? "Cricket" : i === 2 ? "Dance" : "Hackathons";
    m.dislikes = "Long lectures";
    m.domain = i === 3 ? "HealthTech" : "EdTech";
    m.assessment = `Personal goal ${i}`;
    rows.push(m);
  }
  const wb = buildRosterWorkbook(rows, { mode: "individuals" });
  XLSX.writeFile(wb, "individuals_roster_template.xlsx");
}

export function exportRosterSheet(members, opts = {}) {
  const wb = buildRosterWorkbook(members, opts);
  const stamp = new Date().toISOString().slice(0, 10);
  const label = opts.mode === "individuals" ? "individuals" : opts.teamName || "roster";
  const base = String(label).replace(/[^\w\-]+/g, "_").slice(0, 40);
  XLSX.writeFile(wb, `${base || "roster"}_${stamp}.xlsx`);
}
